import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import rootStore from "./store";
import { BaseDashboardLayout, Layout } from "./layout";
import { BaseLayout } from "./layout/baseLayout";
import { ErrorBoundaryPage } from "./layout/errorBoundary";
import { DashboardLoader, IndexLoader, AuthLoader } from "./loaders";
import { IndexPage } from "./pages";
import "react-toastify/dist/ReactToastify.css";
import { BillingSentPage } from "./pages/dashboard/payments/sent";

import { NewInviteHashPage } from "./pages/auth/invite/[hash]";
import { NewAuthHomePage } from "./pages/auth";
import { NewLoginPage } from "./pages/auth/login";
import { NewRegisterPage } from "./pages/auth/register";
import { NewResetPage } from "./pages/auth/reset/reset";
import { NewVerifyPage } from "./pages/auth/reset/verify";
import { PrivacyPolicyPage } from "./pages/auth/privacyPolicy";

import { DashboardDemo } from "./pages/dashboard/dashboard";
import { SelectProfile } from "./pages/dashboard/selectProfile";
import { RequisitesPage } from "./pages/dashboard/requisites";
import { LocationPage } from "./pages/dashboard/location/[id]";
import { OfficePage } from "./pages/dashboard/location/office/[id]";
import { LocksPage } from "./pages/dashboard/locks";
import { LockLogsPage } from "./pages/dashboard/locks/[id]/[id]";
import { LockPasscodesPage } from "./pages/dashboard/locks/[id]/passcodes";
import { TicketsPage } from "./pages/dashboard/tickets";
import { TicketPage } from "./pages/dashboard/ticket/[id]";
import { RentersUsersPage } from "./pages/dashboard/renters/renters";
import { RenterUsersPage } from "./pages/dashboard/renters/[id]/[id]";
import { RenterEmployeesPage } from "./pages/dashboard/renters/[id]/employees";
import { RenterOfficesPage } from "./pages/dashboard/renters/[id]/offices";
import { RenterRequisitesPage } from "./pages/dashboard/renters/[id]/requisites";
import { OfficeTypesPage } from "./pages/dashboard/officeTypes";
import { UserRolePage } from "./pages/dashboard/userRoles";
import { UsersPage } from "./pages/dashboard/users/users";
import { UserProfilePage } from "./pages/dashboard/users/[id]/[id]";
import { UserRolesEditPage } from "./pages/dashboard/users/[id]/roles";
import { BillingPage } from "./pages/dashboard/payments";
import { AgreementsPage } from "./pages/dashboard/agreements";
import { EditAgreementPage } from "./pages/dashboard/agreements/[id]/edit";
import { OfficesPage } from "./pages/dashboard/offices";
import { LocationsPage } from "./pages/dashboard/locations";
import { DocumentTemplatesPage } from "./pages/dashboard/documents";
import { DocumentTemplatePage } from "./pages/dashboard/documents/template";
import { MyProfilePage } from "./pages/dashboard/me";
import { SettingsPage } from "./pages/dashboard/settings";
import { FinancePage } from "./pages/dashboard/finance/finance";
import { MeetingRoomsPage } from "./pages/dashboard/meetingRooms";
import { PrintersPage } from "./pages/dashboard/printers";
import { TestPage } from "./pages/dashboard/test";
import { CreateOfficePage } from "./pages/dashboard/offices/create";
import { EditOfficePage } from "./pages/dashboard/offices/[id]/edit";
import { WorkplacesPage } from "./pages/dashboard/offices/[id]/workplaces";
import { LockSystemsPage } from "./pages/dashboard/service/locks";
import { SigurIntegrationsPage } from "./pages/dashboard/service/sigur";
import { VisitRequestsPage } from "./pages/dashboard/visit-requests";
import { PublicOfficesPage } from "./pages/dashboard/offices/public";
import { PlansPage } from "./pages/dashboard/plans";
import { CreateBusinessProfilePage } from "./pages/createBusinessProfile";
import { InvitationsPage } from "./pages/dashboard/invitations";
import { CreateExistingInvitationPage } from "./pages/dashboard/invitations/existing/create";
import { EditExistingInvitationPage } from "./pages/dashboard/invitations/existing/[id]/edit";
import { CreateNewInvitationPage } from "./pages/dashboard/invitations/new/create";
import { EditNewInvitationPage } from "./pages/dashboard/invitations/new/[id]/edit";
import { SkudPage } from "./pages/dashboard/skud/skud";
import { SkudLogsPage } from "./pages/dashboard/skud/logs";
import { SkudPasscodesPage } from "./pages/dashboard/skud/passcodes";
import { SkudDevicesPage } from "./pages/dashboard/skud/devices";
import { AccessCardsPage } from "./pages/dashboard/skud/access-cards";
import { IssueCardPage } from "./pages/dashboard/skud/issue-card";
import { AccountingIntegrationsPage } from "./pages/dashboard/accounting/integrations";
import { AccountingConflictsPage } from "./pages/dashboard/accounting/conflicts";

const router = createBrowserRouter([
  {
    path: "/",
    element: <BaseLayout />,
    errorElement: <ErrorBoundaryPage />,
    children: [
      { path: "/", loader: AuthLoader, element: <IndexPage /> },
      { path: "/invite/:hash", element: <NewInviteHashPage /> },
      {
        path: "/createBusinessProfile",
        element: <CreateBusinessProfilePage />,
      },
    ],
  },
  {
    path: "/auth",
    element: <BaseLayout />,
    errorElement: <ErrorBoundaryPage />,
    children: [
      { path: "/auth", loader: IndexLoader, element: <NewAuthHomePage /> },
      { path: "/auth/login", loader: IndexLoader, element: <NewLoginPage /> },
      {
        path: "/auth/register",
        loader: IndexLoader,
        element: <NewRegisterPage />,
      },
      { path: "/auth/reset", loader: IndexLoader, element: <NewResetPage /> },
      {
        path: "/auth/reset/:username",
        loader: IndexLoader,
        element: <NewVerifyPage />,
      },
      {
        path: "/auth/privacy",
        loader: IndexLoader,
        element: <PrivacyPolicyPage />,
      },
    ],
  },
  {
    path: "/dashboard",
    element: <Layout />,
    loader: DashboardLoader,
    errorElement: <ErrorBoundaryPage />,
    children: [
      { path: "/dashboard", loader: IndexLoader, element: <DashboardDemo /> },
      {
        path: "/dashboard/requisites",
        loader: IndexLoader,
        element: <RequisitesPage />,
      },
      {
        path: "/dashboard/selectProfile",
        loader: IndexLoader,
        element: <SelectProfile />,
      },
      {
        path: "/dashboard/location/:id",
        loader: IndexLoader,
        element: <LocationPage />,
      },
      {
        path: "/dashboard/location/:id/office/:officeId",
        loader: IndexLoader,
        element: <OfficePage />,
      },
      { path: "/dashboard/locks", loader: IndexLoader, element: <LocksPage /> },
      {
        path: "/dashboard/locks/:id",
        loader: IndexLoader,
        element: <LockLogsPage />,
      },
      {
        path: "/dashboard/locks/:id/passcodes",
        loader: IndexLoader,
        element: <LockPasscodesPage />,
      },
      { path: "/dashboard/skud", loader: IndexLoader, element: <SkudPage /> },
      {
        path: "/dashboard/skud/logs",
        loader: IndexLoader,
        element: <SkudLogsPage />,
      },
      {
        path: "/dashboard/skud/passcodes",
        loader: IndexLoader,
        element: <SkudPasscodesPage />,
      },
      {
        path: "/dashboard/skud/devices",
        loader: IndexLoader,
        element: <SkudDevicesPage />,
      },
      {
        path: "/dashboard/skud/access-cards",
        loader: IndexLoader,
        element: <AccessCardsPage />,
      },
      {
        path: "/dashboard/skud/issue-card",
        loader: IndexLoader,
        element: <IssueCardPage />,
      },
      {
        path: "/dashboard/tickets",
        loader: IndexLoader,
        element: <TicketsPage />,
      },
      {
        path: "/dashboard/tickets/:type/:id",
        loader: IndexLoader,
        element: <TicketPage />,
      },
      {
        path: "/dashboard/renters",
        loader: IndexLoader,
        element: <RentersUsersPage />,
      },
      {
        path: "/dashboard/renters/:id",
        loader: IndexLoader,
        element: <RenterUsersPage />,
      },
      {
        path: "/dashboard/renters/:id/employees",
        loader: IndexLoader,
        element: <RenterEmployeesPage />,
      },
      {
        path: "/dashboard/renters/:id/offices",
        loader: IndexLoader,
        element: <RenterOfficesPage />,
      },
      {
        path: "/dashboard/renters/:id/requisites",
        loader: IndexLoader,
        element: <RenterRequisitesPage />,
      },
      {
        path: "/dashboard/officeTypes",
        loader: IndexLoader,
        element: <OfficeTypesPage />,
      },
      {
        path: "/dashboard/userRoles",
        loader: IndexLoader,
        element: <UserRolePage />,
      },
      { path: "/dashboard/users", loader: IndexLoader, element: <UsersPage /> },
      {
        path: "/dashboard/users/:id",
        loader: IndexLoader,
        element: <UserProfilePage />,
      },
      {
        path: "/dashboard/users/:id/roles",
        loader: IndexLoader,
        element: <UserRolesEditPage />,
      },
      {
        path: "/dashboard/payments",
        loader: IndexLoader,
        element: <BillingPage />,
      },
      {
        path: "/dashboard/payments/sent",
        loader: IndexLoader,
        element: <BillingSentPage />,
      },
      {
        path: "/dashboard/rentedOffices",
        loader: IndexLoader,
        element: <AgreementsPage />,
      },
      {
        path: "/dashboard/agreements/:id/edit",
        loader: IndexLoader,
        element: <EditAgreementPage />,
      },
      {
        path: "/dashboard/offices",
        loader: IndexLoader,
        element: <OfficesPage />,
      },
      {
        path: "/dashboard/locations",
        loader: IndexLoader,
        element: <LocationsPage />,
      },
      {
        path: "/dashboard/documents",
        loader: IndexLoader,
        element: <DocumentTemplatesPage />,
      },
      {
        path: "/dashboard/documents/:id/fill",
        loader: IndexLoader,
        element: <DocumentTemplatePage />,
      },
      {
        path: "/dashboard/me",
        loader: IndexLoader,
        element: <MyProfilePage />,
      },
      {
        path: "/dashboard/settings",
        loader: IndexLoader,
        element: <SettingsPage />,
      },
      {
        path: "/dashboard/finance",
        loader: IndexLoader,
        element: <FinancePage />,
      },
      {
        path: "/dashboard/printers",
        loader: IndexLoader,
        element: <PrintersPage />,
      },
      { path: "/dashboard/test", loader: IndexLoader, element: <TestPage /> },
      {
        path: "/dashboard/offices/create",
        loader: IndexLoader,
        element: <CreateOfficePage />,
      },
      {
        path: "/dashboard/offices/:id/edit",
        loader: IndexLoader,
        element: <EditOfficePage />,
      },
      {
        path: "/dashboard/offices/:id/workplaces",
        loader: IndexLoader,
        element: <WorkplacesPage />,
      },
      {
        path: "/dashboard/service/locks",
        loader: IndexLoader,
        element: <LockSystemsPage />,
      },
      {
        path: "/dashboard/service/sigur",
        loader: IndexLoader,
        element: <SigurIntegrationsPage />,
      },
      {
        path: "/dashboard/visit-requests",
        loader: IndexLoader,
        element: <VisitRequestsPage />,
      },
      { path: "/dashboard/plans", loader: IndexLoader, element: <PlansPage /> },
      {
        path: "/dashboard/accounting/integrations",
        loader: IndexLoader,
        element: <AccountingIntegrationsPage />,
      },
      {
        path: "/dashboard/accounting/integrations/:id/conflicts",
        loader: IndexLoader,
        element: <AccountingConflictsPage />,
      },
      {
        path: "/dashboard/offices/public",
        loader: IndexLoader,
        element: <PublicOfficesPage />,
      },
      {
        path: "/dashboard/invitations",
        loader: IndexLoader,
        element: <InvitationsPage />,
      },
      {
        path: "/dashboard/invitations/existing/create",
        loader: IndexLoader,
        element: <CreateExistingInvitationPage />,
      },
      {
        path: "/dashboard/invitations/existing/:id/edit",
        loader: IndexLoader,
        element: <EditExistingInvitationPage />,
      },
      {
        path: "/dashboard/invitations/new/create",
        loader: IndexLoader,
        element: <CreateNewInvitationPage />,
      },
      {
        path: "/dashboard/invitations/new/:id/edit",
        loader: IndexLoader,
        element: <EditNewInvitationPage />,
      },
    ],
  },
  {
    path: "/dashboard/meetingRooms",
    loader: IndexLoader,
    element: (
      <BaseDashboardLayout
        withNavbar={false}
        withNavigation={false}
        constrainContent={false}
        contentClassName="flex flex-1 flex-col bg-background">
        <MeetingRoomsPage />
      </BaseDashboardLayout>
    ),
  },
]);

export function App() {
  return (
    <Provider store={rootStore}>
      <RouterProvider router={router} />
    </Provider>
  );
}
